import React, { useState } from "react";
import { Upload } from "lucide-react";
import { Box, Text, Flex, Progress } from "@radix-ui/themes";

interface FileUploadZoneProps {
  onFileChange?: (files: File[]) => void | Promise<void>;
  onClearError?: () => void;
  accept?: string;
  maxSize?: number; // in MB
  multiple?: boolean;
  disabled?: boolean;
  loading?: boolean;
  progress?: number | null;
  progressLabel?: string | null;
  className?: string;
  children?: React.ReactNode;
  height?: string;
}

export default function FileUploadZone({
  onFileChange,
  onClearError,
  accept = "*/*",
  maxSize = 10,
  multiple = true,
  disabled = false,
  loading = false,
  progress = null,
  progressLabel = null,
  className = "",
  children,
  height = "8rem",
}: FileUploadZoneProps) {
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>
  ) => {
    e.preventDefault();
    setDragActive(false);

    let fileList: FileList | null = null;

    if ("dataTransfer" in e) {
      fileList = e.dataTransfer?.files;
    } else {
      fileList = e.target.files;
    }

    if (!fileList || fileList.length === 0) return;

    const fileArray = Array.from(fileList);

    // Always call onFileChange - let parent handle validation
    onFileChange?.(fileArray);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled && !loading) {
      setDragActive(true);
      // Clear any existing errors when user starts interacting
      if (onClearError) {
        onClearError();
      }
    }
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleClick = () => {
    if (!disabled && !loading) {
      // Clear any existing errors when user clicks to upload
      if (onClearError) {
        onClearError();
      }
      document.getElementById("file-upload-zone-input")?.click();
    }
  };

  return (
    <Box className={className}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Click to upload ${multiple ? "files" : "a file"}`}
        data-disabled={disabled || loading}
        data-dragging={dragActive}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height,
          border: "2px dashed",
          borderColor: dragActive ? "var(--blue-9)" : "var(--gray-7)",
          borderRadius: "var(--radius-3)",
          backgroundColor: dragActive
            ? "var(--blue-3)"
            : disabled || loading
            ? "var(--gray-3)"
            : "var(--gray-2)",
          cursor: disabled || loading ? "not-allowed" : "pointer",
          opacity: disabled || loading ? 0.6 : 1,
          transition: "all 0.2s ease",
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={!disabled && !loading ? handleFileChange : undefined}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (!disabled && !loading && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {children || (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="2"
            style={{
              padding: "1rem",
              textAlign: "center",
              width: "100%",
            }}
          >
            <Upload
              style={{
                width: "1.5rem",
                height: "1.5rem",
                color: loading ? "var(--gray-9)" : "var(--gray-11)",
              }}
            />
            {loading ? (
              <Flex direction="column" align="center" gap="2" style={{ width: "min(360px, 80%)" }}>
                <Text size="2" weight="medium" color="gray">
                  {progressLabel || "Uploading…"}
                </Text>
                <Progress value={progress ?? undefined} size="2" style={{ width: "100%" }} />
                {progress != null && (
                  <Text size="1" color="gray">{progress}%</Text>
                )}
              </Flex>
            ) : (
              <>
                <Text size="2" color="gray">
                  <Text weight="medium">Drop files here</Text> or click to browse
                </Text>
                <Text size="1" color="gray">
                  Max: {maxSize}MB {accept !== "*/*" && `• ${accept.toUpperCase()}`}
                </Text>
              </>
            )}
          </Flex>
        )}
        <input
          id="file-upload-zone-input"
          type="file"
          accept={accept}
          style={{ display: "none" }}
          multiple={multiple}
          disabled={disabled || loading}
          onChange={handleFileChange}
          tabIndex={-1}
        />
      </Box>
    </Box>
  );
}
